import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { money } from "../money.ts";
import type {
  CartLine,
  CartReview,
  Merchant,
  OrderLineToPlace,
  PaymentInstrument,
  RiderApi,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  Group,
  InfoNote,
  MarkControl,
  MarketShell,
  type BadgeValue,
  type MarketAction,
  type RowValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Checkout` — the screen the measurement was built to notice moving, now drawn.
 *
 * It was graded **blocked** for as long as the measurement has run, and for one reason:
 * `POST /v1/tenants/{tenantId}/commerce-orders` published a request body referencing
 * `#/components/schemas/PlaceOrder`, a schema its own contract never defined. The one command the
 * whole commerce flow exists to reach described nothing a caller could send. The shape was never
 * missing — it was a branch of a `z.discriminatedUnion` in a module-local `const`, and the schema
 * generator publishes exports whose names end in `Schema`. Naming and exporting the branches
 * published it, generated rather than written.
 *
 * It is **degraded**, not buildable, and the two remaining absences are records nothing holds rather
 * than references to repair.
 *
 * | Drawn                                | From                                                     |
 * | ------------------------------------ | -------------------------------------------------------- |
 * | `Pay KES 440`                        | EXP-002's `itemsTotal` — and see the next section        |
 * | `Kilimani Fresh`                     | MER-01's `tradingName`, one request beside the cart      |
 * | `M-Pesa ··· 0421`                    | PAY-04's `provider` and `maskedAccount`                  |
 * | `recommended`                        | PAY-04's `isDefault`                                     |
 * | `Place order`                        | COM-05's command, under the key its contract requires    |
 * | `If an item isn't available…`        | designed copy, word for word                             |
 * | `price held 9:48`                    | **nothing** — no service publishes a hold on a price     |
 * | `Deliver to Kilimani, Rose Ave 12`   | **nothing** — saved places can be written and not read   |
 * | `Card ··· 4417` / `Cash on delivery` | **nothing** — PAY-04 models two providers, neither these |
 *
 * ## The design's figure is not unpublished. It is refused
 *
 * `Pay KES 559` and `Place order · KES 559` are the subtotal plus a delivery fee and a service fee,
 * which is the same fee-inclusive total `Cart` draws as an absence — `chargeableTotalAbsence` says
 * nothing publishes either fee. On this screen it is sharper than an absence, because COM-05's own
 * domain **rejects** that figure: it refuses any order whose `totalAmountMinor` is not the line
 * snapshot's subtotal, in those words. So 559 is not a number this platform has not got around to
 * publishing; it is a number the order command would answer 400 to.
 *
 * This screen therefore draws `Pay KES 440` — the subtotal, which is exactly what COM-05 will accept
 * and record — and carries a flag saying the design's figure adds fees the command refuses. That is
 * the same decision `Cart` records for the design's `KES 700` title, taken again because the
 * evidence is the same: a figure in the design file that another part of the platform contradicts is
 * recorded, not reproduced.
 *
 * ## Two reasons the button refuses before it sends, both of them real
 *
 * `PlaceOrder` requires a `sku` and a `name` on every line, and EXP-002 types both `string | null`.
 * A line COM-02 could not name, or whose SKU it did not answer, cannot be snapshotted — and the only
 * ways to send one anyway are to invent a placeholder SKU or drop the line from the order, which is
 * to charge a rider for a different cart than the one they are looking at. So the button says which
 * line and why.
 */

export interface CheckoutProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly customerId: string;
  readonly cartId: string | undefined;
  /** Where a placed order goes: `Order tracking`, carrying the order it is about. */
  readonly onPlaced: (orderId: string) => void;
  readonly onBack: () => void;
}

/**
 * The design's exact sentence, as its `note` writes it.
 *
 * Kept as one constant so the acceptance check compares one string rather than a built one. The
 * apostrophes are the design's own, copied rather than normalised.
 */
const REFUND_NOTE =
  "If an item isn't available, we'll refund it automatically. You won't be charged for anything not packed.";

/** Nothing on this platform stores a card instrument. `Payment methods` records the same absence. */
const NO_STORED_CARD = "Nothing stores a card instrument: PAY-04 models M-Pesa and Airtel Money";

/** Cash is a way of paying a person, and no service publishes it as a choice on an order. */
const NO_CASH_OPTION =
  "Nothing publishes cash on delivery as a payment choice: COM-05 records no payment method on an order";

/**
 * The two designed rows that are method types rather than records.
 *
 * A module constant for the reason `PaymentMethods`'s own `OTHER_ROWS` gives: nothing they draw
 * depends on data. They are drawn because the design draws three rows in this group and nothing may
 * be removed, and each explains itself rather than appearing to be a choice a rider could make.
 */
const UNSOURCED_METHODS: readonly RowValue[] = [
  {
    id: "card",
    lead: "credit_card",
    title: "Card ··· 4417",
    titleAbsent: NO_STORED_CARD,
    sub: "Visa",
    subAbsent: NO_STORED_CARD,
    control: <MarkControl on={false} label="Pay by card" unavailable={NO_STORED_CARD} />,
  },
  {
    id: "cash",
    lead: "payments",
    title: "Cash on delivery",
    sub: "Pay the courier",
    /*
     * The title is drawn and the mark is not wired, which is the honest split.
     *
     * `Cash on delivery` is a real thing a rider can do and the design's own words for it, so the
     * row is not an absence the way the card row is. What has no source is it being *selected*:
     * COM-05 records no payment method on an order at all, so there is nothing for a mark to mean.
     */
    control: <MarkControl on={false} label="Pay with cash" unavailable={NO_CASH_OPTION} />,
  },
];

export function Checkout(props: CheckoutProps): ReactNode {
  const { api, tenantId, customerId, cartId, onPlaced } = props;

  const [review, setReview] = useState<CartReview | undefined>(undefined);
  const [merchant, setMerchant] = useState<Merchant | undefined>(undefined);
  const [instruments, setInstruments] = useState<readonly PaymentInstrument[]>([]);
  const [chosen, setChosen] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [placing, setPlacing] = useState(false);

  const load = useCallback(() => {
    if (cartId === undefined) return;
    setState({ kind: "loading" });
    api
      .cartReview({ tenantId, customerId, cartId })
      .then((answer) => {
        setReview(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.journey])));
  }, [api, tenantId, customerId, cartId]);

  useEffect(load, [load]);

  /* The shop's trading name, failed softly, for the reason `Cart` gives for the same request. */
  useEffect(() => {
    const merchantId = review?.merchantId;
    if (merchantId === null || merchantId === undefined) return;
    let current = true;
    api
      .merchant(merchantId)
      .then((answer) => {
        if (current) setMerchant(answer);
      })
      .catch(() => {
        if (current) setMerchant(undefined);
      });
    return (): void => {
      current = false;
    };
  }, [api, review?.merchantId]);

  /*
   * The rider's instruments, failed softly to an empty list.
   *
   * A checkout whose payment methods could not be read is still a checkout: COM-05 records no
   * payment method on an order, so this group is what the rider will approve with and not something
   * the command carries. Hardening it into an error would block placing an order on a read the order
   * does not depend on.
   */
  useEffect(() => {
    let current = true;
    api
      .paymentInstruments(tenantId)
      .then((answer) => {
        if (!current) return;
        setInstruments(answer);
        setChosen((answer.find((instrument) => instrument.isDefault) ?? answer[0])?.instrumentId);
      })
      .catch(() => {
        if (current) setInstruments([]);
      });
    return (): void => {
      current = false;
    };
  }, [api, tenantId]);

  const snapshot = useMemo(
    () => (review === undefined ? undefined : linesToPlace(review)),
    [review],
  );

  const place = useCallback(() => {
    if (review === undefined || snapshot === undefined || snapshot.refusal !== undefined) return;
    const merchantId = review.merchantId;
    const total = review.itemsTotal;
    const location = review.lines[0]?.merchantLocationId;
    if (merchantId === null || total === null || location === undefined) return;
    setPlacing(true);
    api
      .placeCommerceOrder(
        {
          tenantId,
          customerId,
          merchantId,
          merchantLocationId: location,
          currency: total.currency,
          lines: snapshot.lines,
          totalAmountMinor: total.amountMinor,
        },
        /*
         * Derived from the cart and its version, not minted, which is `booking.ts`'s reasoning and
         * `Cart`'s: a rider pressing twice on a flaky connection, or this screen re-sending after a
         * reload, must ask for **the same** order rather than a second one. The version is in the
         * key because the same cart at a later version is a different order.
         */
        `commerce-order:${cartId ?? "no-cart"}:v${String(review.version ?? 0)}`,
      )
      .then((order) => {
        setPlacing(false);
        onPlaced(order.orderId);
      })
      .catch((error: unknown) => {
        setPlacing(false);
        setState(stateForError(error, [SCOPES.placeOrder]));
      });
  }, [api, review, snapshot, tenantId, customerId, cartId, onPlaced]);

  if (cartId === undefined) {
    return (
      <MarketState
        state={{
          kind: "error",
          code: "no_cart_in_address",
          message:
            "This screen is about one cart and the address does not name one. COM-04 publishes no listing of a customer's carts, so nothing can find it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading your cart."
        barTitle="Checkout"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || review === undefined || snapshot === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your cart."
        barTitle="Checkout"
        barSub="CityCab"
      />
    );
  }

  const rows: readonly RowValue[] = [
    ...instruments.map((instrument) => instrumentRow(instrument, chosen, setChosen)),
    ...UNSOURCED_METHODS,
  ];

  const refusal = snapshot.refusal ?? missingRecord(review);
  const actions: readonly MarketAction[] = [
    {
      label: payLabel("Place order", review),
      glyph: "lock",
      primary: true,
      ...(refusal !== undefined
        ? { unavailable: refusal }
        : placing
          ? { unavailable: "This order is being placed" }
          : { onPress: place }),
    },
  ];

  return (
    <MarketShell
      screen="checkout"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Checkout"
      barSub={
        merchant === undefined ? (
          <Absent words="No shop name: the cart review publishes merchantId and not a name" />
        ) : (
          merchant.tradingName
        )
      }
      right={{
        glyph: "lock",
        label: "How this is secured",
        unavailable: "No design file draws what is behind this control",
      }}
      /*
       * The design's `caution · price held 9:48`, drawn as the absence it is.
       *
       * COM-04 publishes `expiresAt`, which `Cart` draws as `Held for 25 minutes` — but that is when
       * the **cart** stops existing, not a promise about its price. Nothing publishes
       * `order.priceHeldUntil` or `cart.priceHeldUntil`, and a cart expiry presented as a price hold
       * would be this build telling a rider their total is guaranteed for nine minutes when no
       * service has said so.
       */
      badge={{ kind: "unknown", label: "no price hold is published" }}
      title={payTitle(review)}
      {...(review.itemsTotal === null
        ? { titleAbsent: "No total: the lines are in more than one currency" }
        : {})}
      sub={
        <Absent words="No delivery address: POST /v1/customers/{customerId}/saved-places exists; there is no GET" />
      }
      {...(review.itemsTotal === null
        ? {}
        : {
            flag: "The design adds a delivery and a service fee to this figure. COM-05 refuses any total that is not the sum of the lines.",
          })}
      actions={actions}
    >
      <Group heading="Pay with" rows={rows} />

      <InfoNote text={REFUND_NOTE} />
    </MarketShell>
  );
}

/** `Pay KES 440` — the subtotal, which is what COM-05 will accept. See this file's header. */
function payTitle(review: CartReview): string {
  return review.itemsTotal === null ? "Pay" : `Pay ${money(review.itemsTotal)}`;
}

/** `Place order · KES 440`, losing its figure when there is no single-currency subtotal. */
function payLabel(label: string, review: CartReview): string {
  return review.itemsTotal === null ? label : `${label} · ${money(review.itemsTotal)}`;
}

/**
 * One instrument as the design writes the first row of `Pay with`.
 *
 * The mask is drawn exactly as PAY-04 answers it, elision included, for the reason
 * `PaymentMethods.tsx` records at length: for a browser to shorten an account, the account must
 * first have reached the browser.
 */
function instrumentRow(
  instrument: PaymentInstrument,
  chosen: string | undefined,
  choose: (instrumentId: string) => void,
): RowValue {
  const badge: BadgeValue | undefined = instrument.isDefault
    ? { kind: "done", label: "recommended" }
    : undefined;
  return {
    id: instrument.instrumentId,
    lead: "smartphone",
    title: `${instrument.provider} ${instrument.maskedAccount}`,
    sub: "Approve on your phone",
    ...(badge === undefined ? {} : { badge }),
    control: (
      <MarkControl
        on={chosen === instrument.instrumentId}
        label={`Pay with ${instrument.provider} ${instrument.maskedAccount}`}
        onPress={() => choose(instrument.instrumentId)}
      />
    ),
  };
}

/** What COM-05 will be sent, or the first line that cannot be snapshotted and why. */
function linesToPlace(review: CartReview): {
  readonly lines: readonly OrderLineToPlace[];
  readonly refusal?: string;
} {
  const lines: OrderLineToPlace[] = [];
  for (const line of review.lines) {
    const refusal = lineRefusal(line);
    if (refusal !== undefined) return { lines: [], refusal };
    /*
     * `line.name` and `line.sku` are both `string` here and not `string | null`, because
     * `lineRefusal` returned `undefined` — which it only does when both are present. Narrowed by a
     * local rather than asserted, so a future reason added to `lineRefusal` cannot leave a `null`
     * reaching the snapshot behind a cast that still compiles.
     */
    const { name, sku } = line;
    if (name === null || sku === null) continue;
    lines.push({
      productId: line.productId,
      sku,
      name,
      quantity: line.quantity,
      unitAmountMinor: line.unitPrice.amountMinor,
    });
  }
  return { lines };
}

/**
 * Why one line cannot be part of an immutable snapshot.
 *
 * Named per line rather than counted, so a rider is told which item is the problem. The alternative
 * to refusing is inventing a SKU or quietly dropping the line, and the second is charging for a
 * different cart than the one on screen.
 */
function lineRefusal(line: CartLine): string | undefined {
  const named = line.name ?? "This item";
  if (line.name === null) {
    return `${named} cannot be ordered: COM-02 answered ${line.productAbsence ?? "nothing"} for its name, and COM-05 requires one on every line`;
  }
  if (line.sku === null) {
    return `${named} cannot be ordered: COM-02 did not answer its SKU, and COM-05 requires one on every line`;
  }
  return undefined;
}

/** The order-level records that must be present before a command can be built at all. */
function missingRecord(review: CartReview): string | undefined {
  if (review.lines.length === 0) return "There is nothing in this cart to order";
  if (review.expired) return "This cart has expired, so its prices are no longer the cart's";
  if (review.merchantId === null) {
    return "The cart review did not answer a merchant, and COM-05 requires one";
  }
  if (review.itemsTotal === null) {
    return "The lines are in more than one currency, so there is no total COM-05 would accept";
  }
  return undefined;
}
